import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import * as Types from 'types'
import { compose } from 'redux'
import { connect } from 'react-redux'
import * as flashActions from 'redux-flash'
import { logException } from 'sentry'
import SideNav from './side-nav'
import {
  Content,
  FlashMessageContainer,
  SessionExpiryMonitor,
  ExternalLink,
  AccountMenu,
  Modal,
  RenderedHtml,
  FooterLegalese,
  NotificationsMenu,
  TeachingArtistPortalSelector,
} from 'components'
import { NotificationsPreferencesForm } from 'forms'
import { ARTIST_PORTAL_ROUTE } from 'config'
import { onError } from 'lp-hoc'
import * as apiActions from 'api-actions'
import { replace } from 'react-router-redux'
import { selectors as globalSelectors } from 'global-reducer'
import { selectors as globalArtistSelectors } from 'artist-portal-reducer'
import {
  updateDocumentTitle,
  useCommunity,
  useCommunityText,
  getCurrentEmployment,
  distinctEmploymentPositions,
  displaySubmitFailure,
  initializeAuthenticatedUserflow,
} from 'utils'
import isLearningHubEnabled from 'utils/local/is-learning-hub-enabled'
import PendingApprovalAccountModal from 'components/pending-approval-account-modal'
import { Spinner } from 'lp-components'
import { kebabCase } from 'lodash'
import classnames from 'classnames'
import menuIcon from 'images/menu.svg'

const propTypes = {
  children: PropTypes.node,
  currentArtistId: PropTypes.number.isRequired,
  fetchActiveSchoolYears: PropTypes.func.isRequired,
  fetchUserDetails: PropTypes.func.isRequired,
  flashMessages: PropTypes.arrayOf(flashActions.flashMessageType),
  isAdmin: PropTypes.bool,
  userDetails: Types.user,
  redirectToTerms: PropTypes.func.isRequired,
  location: PropTypes.object.isRequired,
  redirectToSignOut: PropTypes.func.isRequired,
  currentSchoolYear: Types.schoolYear.isRequired,
  fetchArtist: PropTypes.func.isRequired,
  artist: Types.artist,
  fetchNotifications: PropTypes.func.isRequired,
  notifications: PropTypes.arrayOf(Types.notification),
  fetchNotificationPreferences: PropTypes.func.isRequired,
  updateNotificationPreferences: PropTypes.func.isRequired,
  notificationPreferences: Types.notificationPreferences,
  markAllAsRead: PropTypes.func.isRequired,
}

const defaultProps = {
  children: null,
  flashMessages: [],
  isAdmin: false,
  userDetails: null,
}

function LayoutAuthorized({
  children,
  currentArtistId,
  fetchActiveSchoolYears,
  flashMessages,
  fetchUserDetails,
  isAdmin,
  userDetails,
  redirectToTerms,
  location,
  redirectToSignOut,
  currentSchoolYear,
  fetchArtist,
  artist,
  fetchNotifications,
  notifications,
  fetchNotificationPreferences,
  updateNotificationPreferences,
  notificationPreferences,
  markAllAsRead,
}) {
  const community = useCommunity()
  const t = useCommunityText()
  const terms = t('terms')
  const [showTermsModal, setShowTermsModal] = useState(false)
  const [showPreferencesModal, setShowPreferencesModal] = useState(false)
  const [holdForRedirect, setHoldForRedirect] = useState(!!terms)
  const isSignOutPath = location.pathname.includes('sign-out')
  const showNotificationsMenu = community.notificationsEnabled

  const [showPendingApprovalAccountModal, setShowPendingApprovalAccountModal] =
    useState(false)

  useEffect(() => {
    if (isSignOutPath) return
    fetchArtist(currentArtistId)
  }, [currentArtistId, isSignOutPath, fetchArtist])

  useEffect(() => {
    if (artist?.state === 'pending_approval') {
      setShowPendingApprovalAccountModal(true)
    }
  }, [artist?.state])

  useEffect(() => {
    if (isSignOutPath) return
    fetchNotifications()
  }, [isSignOutPath, fetchNotifications])

  useEffect(() => {
    if (isSignOutPath) return
    if (userDetails?.notificationSubscriptionId) {
      fetchNotificationPreferences(userDetails.notificationSubscriptionId)
    }
  }, [
    userDetails?.notificationSubscriptionId,
    isSignOutPath,
    fetchNotificationPreferences,
  ])

  useEffect(() => {
    if (isSignOutPath) return
    fetchActiveSchoolYears({ teachingArtistId: currentArtistId })
  }, [currentArtistId, isSignOutPath, fetchActiveSchoolYears])

  useEffect(() => {
    if (isSignOutPath) return
    if (!userDetails) fetchUserDetails()
  }, [isSignOutPath, userDetails, fetchUserDetails])

  useEffect(() => {
    if (terms && userDetails) {
      if (!userDetails.agreedAt && !location.pathname.includes('sign-out')) {
        redirectToTerms()
      } else {
        setHoldForRedirect(false)
      }
    }
  }, [userDetails, terms])

  const currentEmployment = useMemo(() => {
    if (!userDetails) return
    return getCurrentEmployment({
      employments: userDetails.employments,
      currentEmployableId: currentArtistId,
      currentEmployableType: Types.EMPLOYABLE_TYPES.TEACHING_ARTIST,
    })
  }, [userDetails, currentArtistId])

  const employmentPositions = userDetails
    ? distinctEmploymentPositions(userDetails.employments)
    : []

  const hasLoaded = !!userDetails
  const learningHubAccess = isLearningHubEnabled(userDetails)

  // if a current employment wasn't found, the user is not correctly signed in
  if (hasLoaded && !currentEmployment) redirectToSignOut()

  initializeAuthenticatedUserflow(userDetails)

  const [navVisibleOnMobile, setNavVisibleOnMobile] = useState(false)
  const closeMobileNav = () => {
    setNavVisibleOnMobile(false)
    return true
  }

  return (
    <div className={kebabCase(Types.PORTAL.ARTIST_PORTAL)}>
      <a href="#content" id="skip-nav-link">
        Skip to Main Content
      </a>
      <button
        type="button"
        className="nav-toggle"
        aria-expanded={navVisibleOnMobile}
        aria-controls="side-navigation-content"
        onClick={() => setNavVisibleOnMobile(true)}
        aria-label="Open Navigation"
      >
        <img src={menuIcon} alt="" />
      </button>
      <div
        id="side-navigation-content"
        className={classnames('nav-container', {
          'is-active': navVisibleOnMobile,
        })}
      >
        <button
          type="button"
          className="close"
          aria-label="Close Navigation"
          onClick={closeMobileNav}
        >
          ×
        </button>
        {learningHubAccess && (
          <TeachingArtistPortalSelector
            currentPortal={Types.PORTAL.ARTIST_PORTAL}
            learningHubAccess={learningHubAccess}
          />
        )}
        <SideNav
          community={community}
          currentSchoolYear={currentSchoolYear}
          closeMobileNav={closeMobileNav}
          artistId={currentArtistId}
        />
      </div>
      {!hasLoaded || holdForRedirect ? (
        <Spinner />
      ) : (
        <div
          className={classnames({ 'portal-select-enabled': learningHubAccess })}
        >
          {isAdmin && (
            <div className="flash-message admin failure">
              <p>You are viewing the portal as an admin</p>
            </div>
          )}
          {artist?.state === 'pending_approval' && (
            <div className="flash-message admin failure">
              <p>
                Your account is under review and will appear on the map once
                it's approved.
              </p>
            </div>
          )}
          <FlashMessageContainer messages={flashMessages} />
          <SessionExpiryMonitor redirectToSignOut={redirectToSignOut} />
          <Content id="content" className="portal-wrapper">
            {showNotificationsMenu && notificationPreferences && (
              <NotificationsMenu
                openPreferencesModal={(show) => setShowPreferencesModal(show)}
                notifications={notifications}
                markAllAsRead={markAllAsRead}
              />
            )}
            <AccountMenu
              currentEmployment={currentEmployment}
              currentEmployableType={Types.EMPLOYABLE_TYPES.TEACHING_ARTIST}
              employments={userDetails.employments}
              userProfile={userDetails.profile}
              userEmail={userDetails.email}
            />
            {children}
            <footer className="portal-footer">
              <FooterLegalese
                hasTerms={!!terms}
                openTermsModal={() => setShowTermsModal(true)}
              />
              <p className="contact">
                Email{' '}
                <ExternalLink href={`mailto:${community.artistSupportEmail}`}>
                  {community.artistSupportEmail}
                </ExternalLink>{' '}
                with questions.
              </p>
            </footer>
            {showTermsModal && (
              <Modal onClose={() => setShowTermsModal(false)}>
                <h2>Terms & Conditions</h2>
                <div className="modal-content">
                  <RenderedHtml>{terms}</RenderedHtml>
                </div>
              </Modal>
            )}
            {showPreferencesModal && (
              <Modal onClose={() => setShowPreferencesModal(false)}>
                <NotificationsPreferencesForm
                  type={Types.ARTIST_TYPE}
                  employmentPositions={employmentPositions}
                  initialValues={notificationPreferences}
                  onSubmit={(params) =>
                    updateNotificationPreferences(
                      notificationPreferences.id,
                      params
                    )
                  }
                  onSubmitSuccess={() => setShowPreferencesModal(false)}
                  onSubmitFail={displaySubmitFailure}
                  closePreferencesModal={() => setShowPreferencesModal(false)}
                />
              </Modal>
            )}
            {showPendingApprovalAccountModal && (
              <PendingApprovalAccountModal
                supportEmail={community.artistSupportEmail}
                onClose={() => setShowPendingApprovalAccountModal(false)}
              />
            )}
          </Content>
        </div>
      )}
    </div>
  )
}

LayoutAuthorized.propTypes = propTypes
LayoutAuthorized.defaultProps = defaultProps

function mapStateToProps(state) {
  return {
    userDetails: globalArtistSelectors.userDetails(state),
    flashMessages: flashActions.getFlashMessages(state),
    isAdmin: globalArtistSelectors.isAdmin(state),
    currentSchoolYear: globalSelectors.currentSchoolYear(state),
    currentArtistId: globalArtistSelectors.currentArtistId(state),
    artist: globalArtistSelectors.artist(state),
    notifications: globalArtistSelectors.notifications(state),
    markAllAsRead: apiActions.markAllAsRead,
    notificationPreferences:
      globalArtistSelectors.notificationPreferences(state),
  }
}

const mapDispatchToProps = {
  fetchActiveSchoolYears: apiActions.fetchActiveSchoolYears,
  fetchUserDetails: apiActions.fetchCurrentArtistUserDetails,
  redirectToTerms: () => replace(ARTIST_PORTAL_ROUTE + '/terms'),
  redirectToSignOut: () => replace(ARTIST_PORTAL_ROUTE + '/sign-out'),
  fetchNotifications: apiActions.fetchNotifications,
  fetchNotificationPreferences: apiActions.fetchNotificationPreferences,
  updateNotificationPreferences: apiActions.updateNotificationPreferences,
  fetchArtist: apiActions.fetchArtistAuthorized,
}

function updateTitle({ artist }) {
  const title = artist ? `${artist.name} - ` : ''
  return `${title}Arts Artist | artlook`
}

function onComponentDidCatch(_, error, errorInfo) {
  return logException(error, errorInfo)
}

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  updateDocumentTitle(updateTitle),
  onError(onComponentDidCatch)
)(LayoutAuthorized)
